'use client';

import { useMemo } from 'react';
import { createColumnConfigHelper, DataTableFilter, useDataTableFilters } from '@savvy-kit/templates/data-table-filter';

import type { ColumnOption, FiltersState } from '@savvy-kit/templates/data-table-filter';
import type { TaxEnrollmentStatus, TaxEnrollmentTypeName } from '@savvy/constants/generated/gql-enums';

import { StatusOptions } from './filters/tax-status-filter';
import { useTaxOverviewParams } from './tax-overview-url-state';

const FILING_TYPE_COLUMN_ID = 'enrollmentType';
const STATUS_COLUMN_ID = 'status';
const TAX_YEAR_COLUMN_ID = 'taxYear';

const FILING_TYPE_OPTIONS: ColumnOption[] = [
  { value: 'savvy_single', label: 'Single filing' },
  { value: 'savvy_joint', label: 'Joint filing' },
  { value: 'transcript_services_only', label: 'Transcript only' },
  { value: 'external_cpa', label: 'External CPA' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS: ColumnOption[] = StatusOptions.map(({ value, label }) => ({ value, label }));

const TAX_YEAR_OPTIONS: ColumnOption[] = [
  { value: '2025', label: '2025' },
  { value: '2026', label: '2026' },
];

const VALID_FILING_TYPES = new Set(FILING_TYPE_OPTIONS.map((o) => o.value));
const VALID_STATUSES = new Set(STATUS_OPTIONS.map((o) => o.value));
const VALID_TAX_YEARS = new Set(TAX_YEAR_OPTIONS.map((o) => o.value));

type TaxFilterRow = {
  enrollmentType: string[];
  status: string[];
  taxYear: string[];
};

function stateToFilters(enrollmentType: string[], status: string[], taxYear: string[]): FiltersState {
  const filters: FiltersState = [];
  if (enrollmentType.length > 0) {
    filters.push({
      columnId: FILING_TYPE_COLUMN_ID,
      type: 'multiOption',
      operator: enrollmentType.length > 1 ? 'include any of' : 'include',
      values: [...enrollmentType],
    });
  }
  if (status.length > 0) {
    filters.push({
      columnId: STATUS_COLUMN_ID,
      type: 'multiOption',
      operator: status.length > 1 ? 'include any of' : 'include',
      values: [...status],
    });
  }
  if (taxYear.length > 0) {
    filters.push({
      columnId: TAX_YEAR_COLUMN_ID,
      type: 'multiOption',
      operator: taxYear.length > 1 ? 'include any of' : 'include',
      values: [...taxYear],
    });
  }
  return filters;
}

function filtersToState(filters: FiltersState): {
  enrollmentType: TaxEnrollmentTypeName[];
  status: TaxEnrollmentStatus[];
  taxYear: string[];
} {
  const enrollmentFilter = filters.find((f) => f.columnId === FILING_TYPE_COLUMN_ID);
  const statusFilter = filters.find((f) => f.columnId === STATUS_COLUMN_ID);
  const taxYearFilter = filters.find((f) => f.columnId === TAX_YEAR_COLUMN_ID);

  return {
    enrollmentType:
      enrollmentFilter?.type === 'multiOption'
        ? enrollmentFilter.values.filter(
            (v): v is TaxEnrollmentTypeName => typeof v === 'string' && VALID_FILING_TYPES.has(v),
          )
        : [],
    status:
      statusFilter?.type === 'multiOption'
        ? statusFilter.values.filter((v): v is TaxEnrollmentStatus => typeof v === 'string' && VALID_STATUSES.has(v))
        : [],
    taxYear:
      taxYearFilter?.type === 'multiOption'
        ? taxYearFilter.values.filter((v): v is string => typeof v === 'string' && VALID_TAX_YEARS.has(v))
        : [],
  };
}

export type TaxFilterSavedViewProps = {
  readonly atLimit: boolean;
  readonly activePresetName: string | null;
  readonly isModified: boolean;
  readonly hasFilters: boolean;
  readonly existingNames: string[];
  readonly savedFilters: { id: string; name: string }[];
  readonly onSave: (name: string) => void;
  readonly onUpdate: (id: string) => void;
};

type TaxFilterDropdownProps = {
  readonly savedViews?: TaxFilterSavedViewProps;
  readonly totalCount?: number;
  readonly loading?: boolean;
};

export function TaxFilterDropdown({ savedViews, totalCount, loading }: TaxFilterDropdownProps) {
  const { params, setEnrollmentTypeFilter, setStatusFilter, setTaxYearFilter } = useTaxOverviewParams();

  const currentEnrollmentType = useMemo(() => params.enrollmentTypeFilter ?? [], [params.enrollmentTypeFilter]);
  const currentStatus = useMemo(() => params.statusFilter ?? [], [params.statusFilter]);
  const currentTaxYear = useMemo(() => params.taxYearFilter ?? [], [params.taxYearFilter]);

  const filters = useMemo(
    () => stateToFilters(currentEnrollmentType, currentStatus, currentTaxYear),
    [currentEnrollmentType, currentStatus, currentTaxYear],
  );

  const columnsConfig = useMemo(() => {
    const dtf = createColumnConfigHelper<TaxFilterRow>();
    return [
      dtf
        .multiOption()
        .id(FILING_TYPE_COLUMN_ID)
        .accessor((row) => row.enrollmentType)
        .displayName('Filing Type')
        .options([...FILING_TYPE_OPTIONS])
        .build(),
      dtf
        .multiOption()
        .id(STATUS_COLUMN_ID)
        .accessor((row) => row.status)
        .displayName('Status')
        .options([...STATUS_OPTIONS])
        .build(),
      dtf
        .multiOption()
        .id(TAX_YEAR_COLUMN_ID)
        .accessor((row) => row.taxYear)
        .displayName('Year')
        .options([...TAX_YEAR_OPTIONS])
        .build(),
    ];
  }, []);

  const optionsMap = useMemo(
    () => ({
      [FILING_TYPE_COLUMN_ID]: FILING_TYPE_OPTIONS,
      [STATUS_COLUMN_ID]: STATUS_OPTIONS,
      [TAX_YEAR_COLUMN_ID]: TAX_YEAR_OPTIONS,
    }),
    [],
  );

  const onFiltersChange = useMemo(
    () => (next: FiltersState | ((prev: FiltersState) => FiltersState)) => {
      const nextFilters = typeof next === 'function' ? next(filters) : next;
      const { enrollmentType, status, taxYear } = filtersToState(nextFilters);
      setEnrollmentTypeFilter(enrollmentType.length > 0 ? enrollmentType : null);
      setStatusFilter(status.length > 0 ? status : null);
      setTaxYearFilter(taxYear.length > 0 ? taxYear : null);
    },
    [filters, setEnrollmentTypeFilter, setStatusFilter, setTaxYearFilter],
  );

  const {
    columns,
    filters: hookFilters,
    actions,
    strategy,
  } = useDataTableFilters({
    strategy: 'server',
    data: [],
    columnsConfig,
    filters,
    onFiltersChange,
    options: optionsMap,
  });

  const savedViewsConfig =
    savedViews !== undefined
      ? {
          atLimit: savedViews.atLimit,
          activeViewName: savedViews.activePresetName,
          isModified: savedViews.isModified,
          hasFilters: savedViews.hasFilters,
          existingNames: savedViews.existingNames,
          savedViews: savedViews.savedFilters,
          onSave: savedViews.onSave,
          onUpdate: savedViews.onUpdate,
        }
      : undefined;

  const resultCount =
    totalCount !== undefined
      ? {
          value: totalCount,
          singularLabel: 'enrollment',
          pluralLabel: 'enrollments',
          isLoading: loading ?? false,
        }
      : undefined;

  return (
    <DataTableFilter
      columns={columns}
      filters={hookFilters}
      actions={actions}
      strategy={strategy}
      filterValueVariant="compact"
      showClearFilterButton={false}
      searchableColumnIds={[FILING_TYPE_COLUMN_ID, STATUS_COLUMN_ID, TAX_YEAR_COLUMN_ID]}
      savedViews={savedViewsConfig}
      resultCount={resultCount}
    />
  );
}
